import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, EmptyState, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StateSelect } from "@/components/StateSelect";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { DeleteButton } from "@/components/DeleteButton";
import { CC_ROLES, SCHOOL_CONTACT_ROLES } from "@/lib/constants";
import { fmtDate } from "@/lib/dates";

export default function Schools() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [ccs, setCcs] = useState([]);
  const [schoolUsers, setSchoolUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [query, setQuery] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const isCC = CC_ROLES.includes(user.role);
  const load = () => api.get("/schools").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    if (isCC) {
      api.get("/users", { params: { role: "Campus Coordinator" } }).then(({ data }) => setCcs(data));
      api.get("/users", { params: { role: "School User" } }).then(({ data }) => setSchoolUsers(data));
    }
  }, []);

  const ccName = (id) => ccs.find((c) => c.id === id)?.name || "—";
  const stateOptions = Array.from(new Set(rows.map((s) => (s.state || "").trim()).filter(Boolean))).sort();
  const q = query.trim().toLowerCase();
  const filtered = rows.filter((s) => {
    if (stateFilter !== "all" && (s.state || "").trim() !== stateFilter) return false;
    if (!q) return true;
    return [s.name, s.city, s.state, s.campus_location, ...(s.programs || [])]
      .filter(Boolean).join(" ").toLowerCase().includes(q);
  });
  const DISCIPLINES = ["PT", "PTA", "OT", "OTA", "SLP"];
  const toggleProgram = (d) => {
    const cur = form.programs || [];
    setForm({ ...form, programs: cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d] });
  };

  // An unsaved school has no id to attach contacts to — create it first, then keep editing it.
  const ensureSchoolSaved = async () => {
    if (editing?.id) return editing.id;
    if (!form.name) { toast.error("Enter the school name first."); return null; }
    const { data } = await api.post("/schools", form);
    setEditing({ ...form, id: data.id });
    setForm((f) => ({ ...f, id: data.id }));
    load();
    toast.success("School created");
    return data.id;
  };

  const inviteContact = async (program, key, label) => {
    const c = form.program_contacts?.[program]?.[key] || {};
    if (!c.name || !c.email) return toast.error("Enter this contact's name and email first.");
    try {
      const schoolId = await ensureSchoolSaved();
      if (!schoolId) return;
      const { data } = await api.post("/users", { name: c.name, email: c.email, role: "School User", title: label,
        organization_type: "school", organization_id: schoolId });
      toast.success(data.sso ? `${label} invited — they'll sign in with Microsoft` : `${label} invited — login emailed`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setContact = (program, roleKey, field, value) => {
    setForm((f) => {
      const pc = { ...(f.program_contacts || {}) };
      const prog = { ...(pc[program] || {}) };
      const contact = { ...(prog[roleKey] || {}) };
      contact[field] = value;
      prog[roleKey] = contact;
      pc[program] = prog;
      return { ...f, program_contacts: pc };
    });
  };

  const TITLE_TO_CONTACT_KEY = {
    "Program Director": "program_director",
    "Fieldwork Coordinator/CE": "fieldwork_coordinator",
    "Instructor": "instructor",
    "Career Services Contact": "career_services",
  };

  const autoFillContacts = () => {
    const eligible = schoolUsers.filter((u) => u.title && (editing ? u.organization_id === editing.id : true));
    const pc = { ...(form.program_contacts || {}) };
    let filled = 0;
    (form.programs || []).forEach((prog) => {
      const prog_contacts = { ...(pc[prog] || {}) };
      Object.entries(TITLE_TO_CONTACT_KEY).forEach(([title, key]) => {
        const match = eligible.find((u) => u.title === title);
        const existing = prog_contacts[key] || {};
        if (match && !existing.name && !existing.email) {
          prog_contacts[key] = { name: match.name, email: match.email };
          filled += 1;
        }
      });
      pc[prog] = prog_contacts;
    });
    if (filled === 0) {
      toast.info(eligible.length ? "All matching slots are already filled." : "No School Users with a matching Title found for this school. Set titles on the Users page first.");
      return;
    }
    setForm((f) => ({ ...f, program_contacts: pc }));
    toast.success(`Filled ${filled} contact${filled === 1 ? "" : "s"} from School Users`);
  };

  const openNew = () => { setEditing(null); setForm({}); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...s }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        const { id, created_at, updated_at, ...body } = form;
        await api.patch(`/schools/${editing.id}`, body);
        toast.success("School updated");
      } else {
        await api.post("/schools", form);
        toast.success("School created");
      }
      setOpen(false); setForm({}); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const agreementBadge = (s) => {
    const exp = s.agreement_expiration_date ? new Date(s.agreement_expiration_date) : null;
    const soon = exp && (exp - new Date()) / 86400000 < 90;
    return <span className={`text-xs px-2 py-0.5 rounded-full border ${soon ? "bg-orange-50 text-orange-700 border-orange-200" : "bg-green-50 text-green-700 border-green-200"}`}>{s.affiliation_agreement_status}{soon ? " · expiring" : ""}</span>;
  };

  return (
    <div>
      <PageHeader title="Schools" subtitle="School partners, assigned coordinators & affiliation agreements">
        {isCC && <Link to="/school-import"><Button variant="outline" data-testid="import-schools-btn">Import from spreadsheet</Button></Link>}
        {isCC && <Button onClick={openNew} className="bg-cc-blue text-white" data-testid="add-school-btn">+ Add School</Button>}
      </PageHeader>
      {rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Input placeholder="Search schools…" value={query} onChange={(e) => setQuery(e.target.value)}
            data-testid="school-search" className="max-w-xs h-9" />
          <Select value={stateFilter} onValueChange={setStateFilter}>
            <SelectTrigger className="w-[190px] h-9" data-testid="school-state-filter"><SelectValue placeholder="All states" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All states</SelectItem>
              {stateOptions.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-sm text-zinc-500" data-testid="school-count">{filtered.length} of {rows.length}</span>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {filtered.length === 0 && <Card><EmptyState text={rows.length ? "No schools match your filters." : "No schools."} /></Card>}
        {filtered.map((s) => (
          <Card key={s.id} className="p-0">
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-zinc-900">{s.name}</h3>
                  <p className="text-sm text-zinc-500">{s.city}, {s.state} · {s.campus_location}</p>
                </div>
                <div className="flex items-center gap-2">
                  {agreementBadge(s)}
                  {(isCC || (user.role === "School User" && s.id === user.organization_id)) && <button onClick={() => openEdit(s)} data-testid={`edit-school-${s.id}`} className="text-zinc-400 hover:text-cc-blue"><Pencil className="w-4 h-4" /></button>}
                  {["Super Admin", "Campus Coordinator", "Campus Coordinator Supervisor"].includes(user.role) && <DeleteButton endpoint={`/schools/${s.id}`} label="school" name={s.name} onDeleted={load} testId={`delete-school-${s.id}`} />}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                <div><span className="text-zinc-400 text-xs uppercase">Campus Coordinator</span><div className="font-medium text-cc-blue">{ccName(s.assigned_cc_user_id)}</div></div>
                <div><span className="text-zinc-400 text-xs uppercase">Programs</span><div>{(s.programs || []).join(", ")}</div></div>
                <div><span className="text-zinc-400 text-xs uppercase">Agreement Expires</span><div className="font-mono text-xs">{fmtDate(s.agreement_expiration_date)}</div></div>
              </div>
              {s.program_contacts && Object.keys(s.program_contacts).some((p) => Object.values(s.program_contacts[p] || {}).some((c) => c?.name || c?.email)) && (
                <div className="mt-4 border-t pt-3" data-testid={`school-contacts-${s.id}`}>
                  <span className="text-zinc-400 text-xs uppercase">Program contacts</span>
                  <div className="mt-2 space-y-2">
                    {(s.programs || []).filter((p) => s.program_contacts?.[p]).map((p) => {
                      const roles = SCHOOL_CONTACT_ROLES.filter(([k]) => s.program_contacts[p]?.[k]?.name || s.program_contacts[p]?.[k]?.email);
                      if (roles.length === 0) return null;
                      return (
                        <div key={p} className="text-sm">
                          <span className="font-semibold text-cc-blue">{p}</span>
                          <ul className="mt-0.5 space-y-0.5">
                            {roles.map(([k, label]) => {
                              const c = s.program_contacts[p][k];
                              return <li key={k} className="text-xs text-zinc-600"><span className="text-zinc-400">{label}:</span> {c.name}{c.email ? ` · ${c.email}` : ""}{c.phone ? ` · ${c.phone}` : ""}</li>;
                            })}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto cc-scroll">
          <DialogHeader><DialogTitle>{editing ? "Edit School" : "Add School"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="School name"><Input data-testid="school-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Campus location"><Input value={form.campus_location || ""} onChange={(e) => setForm({ ...form, campus_location: e.target.value })} /></Field>
            <Field label="City"><Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
            <Field label="State"><StateSelect value={form.state} onChange={(v) => setForm({ ...form, state: v })} testId="school-state" /></Field>
            {isCC && (
              <Field label="Assigned Campus Coordinator">
                <Select value={form.assigned_cc_user_id} onValueChange={(v) => setForm({ ...form, assigned_cc_user_id: v })}>
                  <SelectTrigger data-testid="school-cc"><SelectValue placeholder="Select coordinator" /></SelectTrigger>
                  <SelectContent>{ccs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Main phone"><Input value={form.main_phone || ""} onChange={(e) => setForm({ ...form, main_phone: e.target.value })} /></Field>
            <div className="col-span-2">
              <Field label="Programs offered">
                <div className="flex flex-wrap gap-2" data-testid="school-programs">
                  {DISCIPLINES.map((d) => {
                    const on = (form.programs || []).includes(d);
                    return (
                      <button type="button" key={d} data-testid={`program-${d}`} onClick={() => toggleProgram(d)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "bg-cc-blue text-white border-cc-blue" : "bg-white text-zinc-600 border-zinc-300 hover:border-cc-blue"}`}>{d}</button>
                    );
                  })}
                </div>
              </Field>
            </div>
            {(form.programs || []).length > 0 ? (
              <div className="col-span-2 border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs uppercase tracking-wide text-zinc-400 font-semibold">Program contacts</p>
                  {isCC && <Button type="button" variant="outline" size="sm" onClick={autoFillContacts} data-testid="autofill-contacts-btn">Auto-fill from School Users</Button>}
                </div>
                <div className="space-y-4" data-testid="school-program-contacts">
                  {(form.programs || []).map((prog) => (
                    <div key={prog} className="rounded-lg border border-zinc-200 p-3">
                      <p className="text-sm font-semibold text-cc-blue mb-2">{prog}</p>
                      <div className="space-y-2">
                        {SCHOOL_CONTACT_ROLES.map(([key, label]) => {
                          const c = form.program_contacts?.[prog]?.[key] || {};
                          return (
                            <div key={key} className="grid grid-cols-[120px_1fr_1fr_130px_auto] gap-2 items-center">
                              <span className="text-xs text-zinc-500">{label}</span>
                              <Input placeholder="Name" value={c.name || ""} data-testid={`contact-${prog}-${key}-name`} onChange={(e) => setContact(prog, key, "name", e.target.value)} />
                              <Input placeholder="Email" value={c.email || ""} data-testid={`contact-${prog}-${key}-email`} onChange={(e) => setContact(prog, key, "email", e.target.value)} />
                              <Input placeholder="Phone" value={c.phone || ""} data-testid={`contact-${prog}-${key}-phone`} onChange={(e) => setContact(prog, key, "phone", e.target.value)} />
                              <Button type="button" variant="outline" size="sm" className="shrink-0" data-testid={`invite-contact-${prog}-${key}`} onClick={() => inviteContact(prog, key, label)}>Invite</Button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="col-span-2 border-t pt-4">
                <p className="text-xs uppercase tracking-wide text-zinc-400 font-semibold mb-1">Program contacts</p>
                <p className="text-sm text-zinc-400" data-testid="school-program-contacts-hint">Select one or more programs above to add each program's Program Director, Fieldwork Coordinator/CE, Instructor and Career Services contacts.</p>
              </div>
            )}
            <Field label="Agreement status">
              <Select value={form.affiliation_agreement_status} onValueChange={(v) => setForm({ ...form, affiliation_agreement_status: v })}>
                <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>{["active", "expired", "pending renewal", "not on file"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Agreement expires"><Input type="date" value={form.agreement_expiration_date ? form.agreement_expiration_date.slice(0, 10) : ""} onChange={(e) => setForm({ ...form, agreement_expiration_date: e.target.value })} /></Field>
          </div>
          <DialogFooter><Button onClick={save} className="bg-cc-blue text-white" data-testid="save-school-btn">{editing ? "Save Changes" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
